var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import { CounterGraphic } from './CoinsAndCountersManipulative';
import { COUNTER_TYPE_BY_ID, GAME_MARKER_PALETTE, getCoinsAndCountersInstanceKey } from '../constants/coinsAndCountersConstants';
import { saveState } from '../helper/stateManagerHelper';
import { markAsCreated } from '../helper/hooks/useDuplicateAnnounce';
import { announce } from '../helper/announcer';
import ToolTip from '../components/ToolTip';
/**
 * Right-side drawer for the Coins & Counters manipulative.
 *
 * Renders one preview tile per drawer entry the user configured in the
 * popup. Each tile is draggable: the user grabs a tile, drags onto the
 * canvas, releases, and a counter manipulative is created at the drop
 * position from the entry's template settings (color / flip / transparent).
 *
 * Mounted from RightSideToolPanel only when
 *   selectedTool[0] === 'coinsAndCounters_dummyForRightTool'.
 * Other manipulatives never render this component.
 */
var TILE_SIZE = 28;
// Pointer movement >= this many pixels is treated as a drag instead of a
// click. Below this, the release is a click and the counter goes through
// the click-to-add placement path regardless of where the cursor ended up.
var CLICK_DRAG_THRESHOLD_PX = 5;
// Auto-stagger for consecutive click-to-add placements: each new coin is
// shifted +CLICK_STAGGER_PX diagonally from the previous one, wrapping
// back to the base position every CLICK_STAGGER_LIMIT clicks. Keeps
// rapid clicks from piling on the same spot.
var CLICK_STAGGER_PX = 30;
var CLICK_STAGGER_LIMIT = 5;
var CoinsAndCountersToolbar = function () {
    var _a, _b, _c, _d;
    var t = useTranslation().t;
    var _e = useContext(DataContext), coinsAndCountersDrawer = _e.coinsAndCountersDrawer, manipulative = _e.manipulative, setManipulative = _e.setManipulative, setSelectedTool = _e.setSelectedTool;
    var zoomLevel = globalStore.useStoreState(function (s) { return s.playerStore; }).zoomLevel;
    // Builds a label matching the placed coin's aria-label: type + color + instance number.
    var buildCoinLabel = function (entry) {
        var meta = COUNTER_TYPE_BY_ID[entry.counterType];
        var parts = [];
        parts.push((meta === null || meta === void 0 ? void 0 : meta.labelKey) ? t(meta.labelKey).toLowerCase() : entry.counterType);
        if (entry.counterType === 'gameMarker' && entry.color) {
            var colorEntry = GAME_MARKER_PALETTE.find(function (s) { return s.id === entry.color; });
            if (colorEntry)
                parts.push(t(colorEntry.colorLabelKey));
        }
        else if (entry.counterType === 'bean' && entry.color) {
            var colorEntry = GAME_MARKER_PALETTE.find(function (s) { return s.hex === entry.color; });
            if (colorEntry)
                parts.push(t(colorEntry.colorLabelKey));
        }
        parts.push(String(nextInstanceOrdinal(entry.counterType, entry.color)));
        return parts.join(' ');
    };
    // Stable per-type/color placement ordinal for a new instance — one past
    // the highest ordinal already assigned to a same-key peer (not just
    // `peers.length + 1`, so a deleted mid-sequence peer doesn't cause a
    // collision). Drives both the placement-announcement label above and the
    // persisted `instanceOrdinal` new instances are created with, which the
    // widget's aria-label instance number reads independently of z-order.
    var nextInstanceOrdinal = function (counterType, color) {
        var instanceKey = getCoinsAndCountersInstanceKey(counterType, color);
        var peers = manipulative.filter(function (m) {
            if (m.data.type !== 'coinsAndCounters')
                return false;
            var d = m.data;
            return getCoinsAndCountersInstanceKey(d.counterType, d.color) === instanceKey;
        });
        return peers.reduce(function (max, m) { var _a; return Math.max(max, (_a = m.data.instanceOrdinal) !== null && _a !== void 0 ? _a : 0); }, 0) + 1;
    };
    var _f = useState(null), dragState = _f[0], setDragState = _f[1];
    // Tracks which drawer entry is currently hovered (for the tooltip).
    // null = no tile hovered. Spec: "Each icon will have a tool tip" — uses
    // the project's ToolTip component to match Pattern Shapes' drawer.
    var _g = useState(null), hoveredEntryId = _g[0], setHoveredEntryId = _g[1];
    var dragStateRef = useRef(null);
    // iOS Safari fires synthetic mousedown/mouseup ~300 ms after touchend on
    // <button> elements (the legacy "ghost click" cascade). Without a guard,
    // a single tap on a drawer tile triggers onPointerDown twice — once via
    // touchstart, once via the synthetic mousedown — and creates two
    // counters. Track the timestamp of the last touch and ignore mouse
    // events that fire within 500 ms of it. (React's onTouchStart is now
    // passive by default, so preventDefault on it isn't reliable.)
    var lastTouchAtRef = useRef(0);
    // Counter of click-to-add placements. Used to stagger consecutive
    // clicks so they don't pile on top of each other. Wraps every
    // CLICK_STAGGER_LIMIT clicks back to the base position. Reset to 0
    // when the previously-placed coin has been moved away from its
    // placement spot (so a fresh click after rearranging restarts at base).
    var clickCountRef = useRef(0);
    // Last canvas-coord position used by click-to-add. We probe this on
    // the next click to decide whether to keep cycling or reset.
    var lastClickPosRef = useRef(null);
    // Keep ref in sync so window listeners (which capture closure once) read
    // the current cursor position without becoming stale.
    useEffect(function () {
        dragStateRef.current = dragState;
    }, [dragState]);
    var onPointerDown = function (e, entry) {
        // iPad ghost-click guard. Touch fires first; synthetic mouse follows
        // ~300 ms later. Stamp the touch time, and on mouse, bail if we just
        // saw a touch.
        var isTouch = 'touches' in e;
        if (isTouch) {
            lastTouchAtRef.current = Date.now();
        }
        else if (Date.now() - lastTouchAtRef.current < 500) {
            return;
        }
        var point = 'touches' in e ? e.touches[0] : e;
        var initial = {
            entry: entry,
            clientX: point.clientX,
            clientY: point.clientY,
            startClientX: point.clientX,
            startClientY: point.clientY,
            hasMoved: false,
        };
        setDragState(initial);
        dragStateRef.current = initial;
        window.addEventListener('mousemove', onPointerMove);
        window.addEventListener('touchmove', onPointerMove, { passive: false });
        window.addEventListener('mouseup', onPointerUp);
        window.addEventListener('touchend', onPointerUp);
        if ('touches' in e)
            e.preventDefault();
    };
    var onPointerMove = function (e) {
        var current = dragStateRef.current;
        if (!current)
            return;
        var point = 'touches' in e ? e.touches[0] : e;
        var dx = point.clientX - current.startClientX;
        var dy = point.clientY - current.startClientY;
        var hasMoved = current.hasMoved ||
            Math.abs(dx) >= CLICK_DRAG_THRESHOLD_PX ||
            Math.abs(dy) >= CLICK_DRAG_THRESHOLD_PX;
        var next = __assign(__assign({}, current), { clientX: point.clientX, clientY: point.clientY, hasMoved: hasMoved });
        dragStateRef.current = next;
        setDragState(next);
        if ('touches' in e)
            e.preventDefault();
    };
    // Shared placement logic for both click-to-add and keyboard Enter.
    // Computes a default canvas position (left of the drawer, vertically
    // centred, with stagger for rapid consecutive placements) and creates
    // the counter manipulative.
    var placeAtDefaultPosition = function (entry) {
        var _a, _b, _c, _d, _e;
        var canvasEl = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
        var rect = canvasEl === null || canvasEl === void 0 ? void 0 : canvasEl.getBoundingClientRect();
        var COIN_MARGIN_FROM_DRAWER = 120;
        var FALLBACK_FROM_LEFT_PX = 200;
        var scrollLeft = (_a = canvasEl === null || canvasEl === void 0 ? void 0 : canvasEl.scrollLeft) !== null && _a !== void 0 ? _a : 0;
        var scrollTop = (_b = canvasEl === null || canvasEl === void 0 ? void 0 : canvasEl.scrollTop) !== null && _b !== void 0 ? _b : 0;
        var visibleH = (_d = (_c = canvasEl === null || canvasEl === void 0 ? void 0 : canvasEl.clientHeight) !== null && _c !== void 0 ? _c : rect === null || rect === void 0 ? void 0 : rect.height) !== null && _d !== void 0 ? _d : 0;
        var drawerToggle = document.querySelector('.drawer-toggle-btn');
        var drawerRect = drawerToggle === null || drawerToggle === void 0 ? void 0 : drawerToggle.getBoundingClientRect();
        var placementX;
        var placementY = (scrollTop + visibleH * 0.5) / zoomLevel;
        if (drawerRect && rect) {
            var drawerLeftCanvasX = (drawerRect.left - rect.left + scrollLeft) / zoomLevel;
            placementX = drawerLeftCanvasX - COIN_MARGIN_FROM_DRAWER;
        }
        else {
            placementX = (scrollLeft + FALLBACK_FROM_LEFT_PX) / zoomLevel;
        }
        var last = lastClickPosRef.current;
        var TOLERANCE_PX = 1;
        var prevStillThere = last !== null &&
            manipulative.some(function (m) {
                var d = m.data;
                return ((d === null || d === void 0 ? void 0 : d.type) === 'coinsAndCounters' &&
                    d.center &&
                    Math.abs(d.center.x - last.x) < TOLERANCE_PX &&
                    Math.abs(d.center.y - last.y) < TOLERANCE_PX);
            });
        if (!prevStillThere)
            clickCountRef.current = 0;
        var stagger = (clickCountRef.current % CLICK_STAGGER_LIMIT) * CLICK_STAGGER_PX;
        placementX += stagger;
        placementY += stagger;
        lastClickPosRef.current = { x: placementX, y: placementY };
        clickCountRef.current += 1;
        var meta = COUNTER_TYPE_BY_ID[entry.counterType];
        var newId = "coinsAndCounters_".concat(uuidv4().replace(/-/g, ''));
        var data = {
            id: newId,
            type: 'coinsAndCounters',
            counterType: entry.counterType,
            scale: 1,
            rotation: 0,
            center: { x: placementX, y: placementY },
            dimensions: meta.defaultDimensions,
            lock: { positioning: false, editing: false },
            flipEnabled: !!entry.flipEnabled,
            currentSide: 'front',
            color: entry.color,
            backColor: entry.backColor,
            transparent: !!entry.transparent,
            instanceOrdinal: nextInstanceOrdinal(entry.counterType, entry.color),
        };
        var newItem = {
            type: 'coinsAndCounters',
            widgetId: "coinsAndCounters:".concat(uuidv4()),
            data: data,
        };
        saveState({
            type: newId,
            id: (_e = newId.split('_')[1]) !== null && _e !== void 0 ? _e : newId,
            propertyName: 'create',
            old: JSON.stringify(newItem),
        });
        markAsCreated(newId);
        announce(t('text.manipulativeAdded', { name: buildCoinLabel(entry) }));
        setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newItem], false); });
        setSelectedTool([newId]);
        Promise.resolve().then(function () {
            setSelectedTool(['coinsAndCounters_dummyForRightTool']);
        });
    };
    var onPointerUp = function (e) {
        var _a, _b, _c, _d, _e, _f, _g;
        window.removeEventListener('mousemove', onPointerMove);
        window.removeEventListener('touchmove', onPointerMove);
        window.removeEventListener('mouseup', onPointerUp);
        window.removeEventListener('touchend', onPointerUp);
        var current = dragStateRef.current;
        if (!current)
            return;
        var point = 'changedTouches' in e
            ? e.changedTouches[0]
            : e;
        var canvasEl = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
        var rect = canvasEl === null || canvasEl === void 0 ? void 0 : canvasEl.getBoundingClientRect();
        var dropX = 0;
        var dropY = 0;
        if (rect) {
            var scrollLeft = (_a = canvasEl === null || canvasEl === void 0 ? void 0 : canvasEl.scrollLeft) !== null && _a !== void 0 ? _a : 0;
            var scrollTop = (_b = canvasEl === null || canvasEl === void 0 ? void 0 : canvasEl.scrollTop) !== null && _b !== void 0 ? _b : 0;
            dropX = (point.clientX - rect.left + scrollLeft) / zoomLevel;
            dropY = (point.clientY - rect.top + scrollTop) / zoomLevel;
        }
        if (current.hasMoved) {
            // Drag-to-drop: place at cursor if it landed on the canvas.
            var COIN_MARGIN_FROM_DRAWER = 120;
            var FALLBACK_FROM_LEFT_PX = 200;
            var scrollLeft = (_c = canvasEl === null || canvasEl === void 0 ? void 0 : canvasEl.scrollLeft) !== null && _c !== void 0 ? _c : 0;
            var scrollTop = (_d = canvasEl === null || canvasEl === void 0 ? void 0 : canvasEl.scrollTop) !== null && _d !== void 0 ? _d : 0;
            var visibleH = (_f = (_e = canvasEl === null || canvasEl === void 0 ? void 0 : canvasEl.clientHeight) !== null && _e !== void 0 ? _e : rect === null || rect === void 0 ? void 0 : rect.height) !== null && _f !== void 0 ? _f : 0;
            var drawerToggle = document.querySelector('.drawer-toggle-btn');
            var drawerRect = drawerToggle === null || drawerToggle === void 0 ? void 0 : drawerToggle.getBoundingClientRect();
            var placementX = void 0;
            var placementY = (scrollTop + visibleH * 0.5) / zoomLevel;
            if (drawerRect && rect) {
                var drawerLeftCanvasX = (drawerRect.left - rect.left + scrollLeft) / zoomLevel;
                placementX = drawerLeftCanvasX - COIN_MARGIN_FROM_DRAWER;
            }
            else {
                placementX = (scrollLeft + FALLBACK_FROM_LEFT_PX) / zoomLevel;
            }
            var overCanvas = rect &&
                point.clientX >= rect.left &&
                point.clientX <= rect.right &&
                point.clientY >= rect.top &&
                point.clientY <= rect.bottom;
            if (overCanvas) {
                placementX = dropX;
                placementY = dropY;
            }
            var meta = COUNTER_TYPE_BY_ID[current.entry.counterType];
            var newId = "coinsAndCounters_".concat(uuidv4().replace(/-/g, ''));
            var data = {
                id: newId,
                type: 'coinsAndCounters',
                counterType: current.entry.counterType,
                scale: 1,
                rotation: 0,
                center: { x: placementX, y: placementY },
                dimensions: meta.defaultDimensions,
                lock: { positioning: false, editing: false },
                flipEnabled: !!current.entry.flipEnabled,
                currentSide: 'front',
                color: current.entry.color,
                backColor: current.entry.backColor,
                transparent: !!current.entry.transparent,
                instanceOrdinal: nextInstanceOrdinal(current.entry.counterType, current.entry.color),
            };
            var newItem_1 = {
                type: 'coinsAndCounters',
                widgetId: "coinsAndCounters:".concat(uuidv4()),
                data: data,
            };
            saveState({
                type: newId,
                id: (_g = newId.split('_')[1]) !== null && _g !== void 0 ? _g : newId,
                propertyName: 'create',
                old: JSON.stringify(newItem_1),
            });
            markAsCreated(newId);
            announce(t('text.manipulativeAdded', { name: buildCoinLabel(current.entry) }));
            setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newItem_1], false); });
            setSelectedTool([newId]);
            Promise.resolve().then(function () {
                setSelectedTool(['coinsAndCounters_dummyForRightTool']);
            });
        }
        else {
            // Click (no drag) — use the shared default-position placement.
            placeAtDefaultPosition(current.entry);
        }
        dragStateRef.current = null;
        setDragState(null);
    };
    // Cleanup on unmount.
    useEffect(function () {
        return function () {
            window.removeEventListener('mousemove', onPointerMove);
            window.removeEventListener('touchmove', onPointerMove);
            window.removeEventListener('mouseup', onPointerUp);
            window.removeEventListener('touchend', onPointerUp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (!coinsAndCountersDrawer || coinsAndCountersDrawer.length === 0) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "ccDrawer", children: coinsAndCountersDrawer.map(function (entry) {
                    var meta = COUNTER_TYPE_BY_ID[entry.counterType];
                    var isDragging = (dragState === null || dragState === void 0 ? void 0 : dragState.entry.id) === entry.id;
                    var tooltipText = (meta === null || meta === void 0 ? void 0 : meta.labelKey) ? t(meta.labelKey) : entry.counterType;
                    return (_jsxs("button", { type: "button", className: "ccDrawerItem ".concat(isDragging ? 'ccDrawerItemDragging' : ''), "data-counter-type": entry.counterType, "aria-label": tooltipText.toLowerCase(), onMouseDown: function (e) { return onPointerDown(e, entry); }, onTouchStart: function (e) { return onPointerDown(e, entry); }, onKeyDown: function (e) {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                placeAtDefaultPosition(entry);
                            }
                        }, onMouseEnter: function () { return setHoveredEntryId(entry.id); }, onMouseLeave: function () { return setHoveredEntryId(null); }, onFocus: function () { return setHoveredEntryId(entry.id); }, onBlur: function () { return setHoveredEntryId(null); }, children: [_jsx("span", { "aria-hidden": "true", children: _jsx(CounterGraphic, { counterType: entry.counterType, width: TILE_SIZE, height: TILE_SIZE, side: "front", color: entry.color, backColor: entry.backColor, transparent: entry.transparent }) }), _jsx(ToolTip, { buttonHovered: hoveredEntryId === entry.id && !dragState, text: tooltipText, direction: "left", isParentRelative: true })] }, entry.id));
                }) }), dragState &&
                createPortal(_jsx("div", { className: "ccDrawerGhost", style: { left: dragState.clientX, top: dragState.clientY }, children: _jsx(CounterGraphic, { counterType: dragState.entry.counterType, width: (_b = (_a = COUNTER_TYPE_BY_ID[dragState.entry.counterType]) === null || _a === void 0 ? void 0 : _a.defaultDimensions.width) !== null && _b !== void 0 ? _b : 60, height: (_d = (_c = COUNTER_TYPE_BY_ID[dragState.entry.counterType]) === null || _c === void 0 ? void 0 : _c.defaultDimensions.height) !== null && _d !== void 0 ? _d : 60, side: "front", color: dragState.entry.color, backColor: dragState.entry.backColor, transparent: dragState.entry.transparent }) }), document.body)] }));
};
export default CoinsAndCountersToolbar;
